import { TFunction } from 'i18next';
import { MasterReserveResponseDto } from 'libs/pinnacle-sdk';
import * as yup from 'yup';

export const initialValues = {
  indemnityMaterialDamage: undefined,
  indemnityBusinessInterruption: undefined,
  underliningSirGovtProgramsSir: undefined,
  reservingComments: undefined,
  lossAdjustorCosts: undefined,
  legalExpertsCosts: undefined,
  consultantsCosts: undefined,
  otherFees: undefined,
  claimsFeesComments: undefined,
  programCessionDeclarationName: undefined,
  programCessionPercent: undefined,
  captiveSirsAag: undefined,
  riSirAag: undefined,
  riCessionPercent: undefined,
  projectedUltimateLoss: undefined,
  recoveryPotential: undefined,
  recoveryActual: undefined,
  recoveryComments: undefined,
};

export const getValidationSchema = (t: TFunction) =>
  yup.object().shape({
    indemnityMaterialDamage: yup
      .number()
      .optional()
      .nullable()
      .min(
        0,
        t('validation.min', {
          field: t('claim.masterReserves.indemnity.indemnityMaterialDamage'),
          min: 0,
        })
      ),
    indemnityBusinessInterruption: yup
      .number()
      .optional()
      .nullable()
      .min(
        0,
        t('validation.min', {
          field: t(
            'claim.masterReserves.indemnity.indemnityBusinessInterruption'
          ),
          min: 0,
        })
      ),
    underliningSirGovtProgramsSir: yup
      .number()
      .optional()
      .nullable()
      .min(
        0,
        t('validation.min', {
          field: t(
            'claim.masterReserves.indemnity.underliningSirGovtProgramsSir'
          ),
          min: 0,
        })
      ),
    reservingComments: yup.string().optional(),
    lossAdjustorCosts: yup
      .number()
      .optional()
      .nullable()
      .min(
        0,
        t('validation.min', {
          field: t('claim.masterReserves.costs.lossAdjustorCosts'),
          min: 0,
        })
      ),
    legalExpertsCosts: yup
      .number()
      .optional()
      .nullable()
      .min(
        0,
        t('validation.min', {
          field: t('claim.masterReserves.costs.legalExpertsCosts'),
          min: 0,
        })
      ),
    consultantsCosts: yup
      .number()
      .optional()
      .nullable()
      .min(
        0,
        t('validation.min', {
          field: t('claim.masterReserves.costs.consultantsCosts'),
          min: 0,
        })
      ),
    otherFees: yup
      .number()
      .optional()
      .nullable()
      .min(
        0,
        t('validation.min', {
          field: t('claim.masterReserves.costs.otherFees'),
          min: 0,
        })
      ),
    claimsFeesComments: yup.string().optional(),
    programCessionDeclarationName: yup.string().optional(),
    programCessionPercent: yup
      .number()
      .optional()
      .nullable()
      .min(
        0,
        t('validation.min', {
          field: t('claim.masterReserves.programRi.programCessionPercent'),
          min: 0,
        })
      )
      .max(
        100,
        t('validation.max', {
          field: t('claim.masterReserves.programRi.programCessionPercent'),
          max: 100,
        })
      ),
    captiveSirsAag: yup
      .number()
      .optional()
      .nullable()
      .min(
        0,
        t('validation.min', {
          field: t('claim.masterReserves.programRi.captiveSirsAag'),
          min: 0,
        })
      ),
    riSirAag: yup
      .number()
      .optional()
      .nullable()
      .min(
        0,
        t('validation.min', {
          field: t('claim.masterReserves.programRi.riSirAag'),
          min: 0,
        })
      ),
    riCessionPercent: yup
      .number()
      .optional()
      .nullable()
      .min(
        0,
        t('validation.min', {
          field: t('claim.masterReserves.programRi.riCessionPercent'),
          min: 0,
        })
      )
      .max(
        100,
        t('validation.max', {
          field: t('claim.masterReserves.programRi.riCessionPercent'),
          max: 100,
        })
      ),
    projectedUltimateLoss: yup
      .number()
      .optional()
      .nullable()
      .min(
        0,
        t('validation.min', {
          field: t('claim.masterReserves.financials.projectedUltimateLoss'),
          min: 0,
        })
      ),
    recoveryPotential: yup
      .number()
      .optional()
      .nullable()
      .min(
        0,
        t('validation.min', {
          field: t('claim.masterReserves.financials.recoveryPotential'),
          min: 0,
        })
      ),
    recoveryActual: yup
      .number()
      .optional()
      .nullable()
      .min(
        0,
        t('validation.min', {
          field: t('claim.masterReserves.financials.recoveryActual'),
          min: 0,
        })
      ),
    recoveryComments: yup.string().optional(),
  });

export const fields = Object.keys(
  initialValues
) as (keyof typeof initialValues)[];

export const roundMasterReserveValues = (
  masterReserve: MasterReserveResponseDto,
  decimalPlaces: number = 0
): MasterReserveResponseDto => {
  const keysToRound = [
    'indemnityMaterialDamage',
    'indemnityBusinessInterruption',
    'underliningSirGovtProgramsSir',
    'otherFees',
    'lossAdjustorCosts',
    'legalExpertsCosts',
    'consultantsCosts',
    'captiveSirsAag',
    'riSirAag',
    'projectedUltimateLoss',
    'recoveryPotential',
    'recoveryActual',
    'totalIncurredCosts',
    'totalFguMarketLossIndemnityExcCosts',
    'totalFguMarketLoss',
  ] as const;

  const roundedMasterReserve = { ...masterReserve };

  keysToRound.forEach((key) => {
    roundedMasterReserve[key] = Number(masterReserve[key] || 0).toFixed(
      decimalPlaces
    );
  });

  return roundedMasterReserve;
};
